'use client';

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from 'react';

import { Overlay, type OverlayLabels } from '@/components/patterns/Overlay';
import {
  GA_EVENTS,
  GA_PARAMS,
  type GaOverlayCloseMethod,
  type GaOverlayNavMethod,
  type GaOverlaySet,
} from '@/constants/analytics';
import { trackEvent } from '@/lib/analytics/trackEvent';

import styles from './OverlayDeck.module.css';

import type { ResolvedOverlayEntry } from '@/content/data/overlay-entries';

export type OverlayTriggerProps = {
  'data-overlay-trigger-index': string;
  'aria-expanded': boolean;
  'aria-haspopup': 'dialog';
};

export type OverlayTriggerActions = {
  /** Spread onto the trigger element (button, circle, etc.). Opens the overlay on click. */
  triggerProps: OverlayTriggerProps;
  /** Whether any entry in this deck is open. */
  isOpen: boolean;
  /** Whether this entry is the one currently shown. */
  isActive: boolean;
};

export type OverlayDeckProps = {
  /** Ordered, translated entries for this deck instance (e.g. actions-only or enablers-only). */
  entries: readonly ResolvedOverlayEntry[];
  labels: OverlayLabels;
  /** GA4 `overlay_set` for open/navigate/close events. */
  overlaySet: GaOverlaySet;
  /**
   * Render prop for each entry's trigger UI. The deck owns open state, index, and
   * prev/next cycling; consumers supply buttons, circles, etc.
   */
  renderTrigger: (entry: ResolvedOverlayEntry, actions: OverlayTriggerActions) => React.ReactNode;
  /**
   * Optional layout wrapper for the ordered trigger nodes (e.g. staggered rows).
   * Click delegation still lives on the outer triggers host.
   */
  renderTriggers?: (triggers: React.ReactNode[]) => React.ReactNode;
  className?: string;
};

function wrapIndex(index: number, length: number): number {
  if (length === 0) return 0;
  return ((index % length) + length) % length;
}

function ensureTriggerId(trigger: HTMLElement, entryId: string): string {
  if (trigger.id) return trigger.id;
  const id = `overlay-trigger-${entryId}`;
  trigger.id = id;
  return id;
}

/**
 * Shared overlay controller for the Response button grid and enabling circles.
 */
export function OverlayDeck({
  entries,
  labels,
  overlaySet,
  renderTrigger,
  renderTriggers,
  className,
}: OverlayDeckProps) {
  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => a.order - b.order),
    [entries],
  );

  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<1 | -1>(1);
  const focusReturnIdRef = useRef<string | null>(null);
  const currentIndexRef = useRef(currentIndex);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const currentEntry = sortedEntries[currentIndex];
  const previousEntry = sortedEntries[wrapIndex(currentIndex - 1, sortedEntries.length)];
  const nextEntry = sortedEntries[wrapIndex(currentIndex + 1, sortedEntries.length)];

  const close = useCallback(
    (method: GaOverlayCloseMethod) => {
      if (currentEntry) {
        trackEvent(GA_EVENTS.overlayClose, {
          [GA_PARAMS.overlaySet]: overlaySet,
          [GA_PARAMS.overlayEntryId]: currentEntry.id,
          [GA_PARAMS.method]: method,
        });
      }
      setOpen(false);
      const returnId = focusReturnIdRef.current;
      focusReturnIdRef.current = null;
      if (!returnId) return;
      requestAnimationFrame(() => {
        document.getElementById(returnId)?.focus();
      });
    },
    [currentEntry, overlaySet],
  );

  const handleTriggerClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const trigger = (event.target as HTMLElement).closest<HTMLElement>(
        '[data-overlay-trigger-index]',
      );
      if (!trigger) return;

      const index = Number.parseInt(trigger.dataset.overlayTriggerIndex ?? '', 10);
      if (Number.isNaN(index) || index < 0 || index >= sortedEntries.length) return;

      const entry = sortedEntries[index];
      focusReturnIdRef.current = ensureTriggerId(
        trigger,
        entry?.id ?? String(index),
      );
      setSlideDirection(1);
      currentIndexRef.current = index;
      setCurrentIndex(index);
      setOpen(true);
      if (entry) {
        trackEvent(GA_EVENTS.overlayOpen, {
          [GA_PARAMS.overlaySet]: overlaySet,
          [GA_PARAMS.overlayEntryId]: entry.id,
        });
      }
    },
    [overlaySet, sortedEntries],
  );

  const goPrevious = useCallback(
    (method: GaOverlayNavMethod) => {
      if (sortedEntries.length === 0) return;
      const nextIndex = wrapIndex(currentIndexRef.current - 1, sortedEntries.length);
      currentIndexRef.current = nextIndex;
      setSlideDirection(-1);
      setCurrentIndex(nextIndex);
      const entry = sortedEntries[nextIndex];
      if (entry) {
        trackEvent(GA_EVENTS.overlayNavigate, {
          [GA_PARAMS.overlaySet]: overlaySet,
          [GA_PARAMS.overlayEntryId]: entry.id,
          [GA_PARAMS.method]: method,
        });
      }
    },
    [overlaySet, sortedEntries],
  );

  const goNext = useCallback(
    (method: GaOverlayNavMethod) => {
      if (sortedEntries.length === 0) return;
      const nextIndex = wrapIndex(currentIndexRef.current + 1, sortedEntries.length);
      currentIndexRef.current = nextIndex;
      setSlideDirection(1);
      setCurrentIndex(nextIndex);
      const entry = sortedEntries[nextIndex];
      if (entry) {
        trackEvent(GA_EVENTS.overlayNavigate, {
          [GA_PARAMS.overlaySet]: overlaySet,
          [GA_PARAMS.overlayEntryId]: entry.id,
          [GA_PARAMS.method]: method,
        });
      }
    },
    [overlaySet, sortedEntries],
  );

  useEffect(() => {
    if (!open || sortedEntries.length === 0) return;

    const preloadEntry = (index: number) => {
      const entry = sortedEntries[wrapIndex(index, sortedEntries.length)];
      const widest = entry.mediaProps?.sources
        .slice()
        .sort((a, b) => a.width - b.width)
        .at(-1);
      if (!widest) return;
      const image = new Image();
      image.src = widest.src;
    };

    preloadEntry(currentIndex - 1);
    preloadEntry(currentIndex + 1);
  }, [currentIndex, open, sortedEntries]);

  if (sortedEntries.length === 0) {
    return null;
  }

  const triggerNodes = sortedEntries.map((entry, index) => (
    <Fragment key={entry.id}>
      {renderTrigger(entry, {
        triggerProps: {
          'data-overlay-trigger-index': String(index),
          'aria-expanded': open && currentIndex === index,
          'aria-haspopup': 'dialog',
        },
        isOpen: open,
        isActive: open && currentIndex === index,
      })}
    </Fragment>
  ));

  return (
    <>
      <div
        className={[styles.triggers, renderTriggers ? undefined : className]
          .filter(Boolean)
          .join(' ')}
        onClick={handleTriggerClick}
      >
        {renderTriggers ? renderTriggers(triggerNodes) : triggerNodes}
      </div>

      {currentEntry ? (
        <Overlay
          open={open}
          entry={currentEntry}
          labels={labels}
          previousTitle={previousEntry?.title ?? ''}
          nextTitle={nextEntry?.title ?? ''}
          slideDirection={slideDirection}
          onClose={close}
          onPrevious={goPrevious}
          onNext={goNext}
        />
      ) : null}
    </>
  );
}
